import { FC } from 'react';
import { Box, Stack, Typography, Button, DialogContent } from '@mui/material';
import { FileUploader } from 'react-drag-drop-files';
import { toast } from 'react-toastify';
import { useTranslation, Trans } from 'react-i18next';

import { ViewNavigationBar } from '@components';
import { useArtworkStepperStore } from '../stores/ArtworkStepperStore';

type Props = {
  onClose: (confirmClose?: boolean) => void;
};

/**
 * First step of the photo artwork stepper: lets the user pick a photo
 * (drag & drop or file picker) and advances to the editor step.
 *
 * The selected File is copied into memory (via arrayBuffer) before it is
 * stored, because the original File is only a reference into the OS file
 * system and can become unreadable before the editor reads it (common on
 * Android after camera/gallery round-trips, see Sentry NPT-CLIENT-H).
 * If even this initial read fails, a toast asks the user to re-select.
 */
export const UploadPhotoStep: FC<Props> = ({ onClose }) => {
  const { setImageFile, setActiveStep } = useArtworkStepperStore();
  const { t } = useTranslation();
  const fileTypes = ['JPG', 'JPEG', 'PNG'];

  const handleChange = async (fileOrFiles: File | File[]) => {
    const file = Array.isArray(fileOrFiles) ? fileOrFiles[0] : fileOrFiles;
    if (!file) {
      return;
    }
    try {
      const buffer = await file.arrayBuffer();
      setImageFile(new File([buffer], file.name, { type: file.type }));
    } catch {
      toast.error(t('fileNotReadableError'));
      return;
    }
    setActiveStep(1);
  };

  return (
    <>
      <ViewNavigationBar
        onClose={() => onClose()}
        title={t('selectYourPhoto')}
      />
      <DialogContent
        sx={{
          height: {
            xs: 'auto',
            md: 'calc(100vh - 200px)',
          },
          padding: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: '#FFFFFF',
        }}
      >
        <Box
          sx={{
            height: '100%',
            ' & .file-uploader': {
              height: '100%',
              width: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              '&:focus-visible': {
                outline: 'none',
              },
              '&:focus-within': {
                outline: 'none',
              },
            },
          }}
        >
          <FileUploader
            classes="file-uploader"
            handleChange={handleChange}
            name="file"
            types={fileTypes}
            maxSize={10} // 10MB
            onSizeError={() => {
              toast.error(t('fileTooLargeError'));
            }}
            onTypeError={() => {
              toast.error(t('invalidFileTypeError'));
            }}
            hoverTitle={t('dropFileHere')}
          >
            <Box
              sx={{
                display: 'flex',
                height: { xs: 300, md: 400 },
                width: { xs: 300, md: 400 },
                border: '10px solid #ECF1FC',
                borderRadius: 400,
                borderStyle: 'dashed',
                justifyContent: 'center',
                alignItems: 'center',
              }}
            >
              <Stack
                direction="column"
                spacing={6}
                alignItems="center"
                justifyContent="center"
              >
                <Button variant="contained">{t('selectYourPhoto')}</Button>
                <Stack spacing={6} direction="row">
                  <Typography
                    sx={{ fontSize: 18, fontWeight: 400, textAlign: 'center' }}
                  >
                    <Trans i18nKey="dragAndDropFileHere" />
                  </Typography>
                </Stack>
              </Stack>
            </Box>
          </FileUploader>
        </Box>
      </DialogContent>
    </>
  );
};
